import type { Devise } from '@artosera/shared';
import { useQuery } from '@tanstack/react-query';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, precedentDansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
import type {
  AvoirRecent,
  FacturePresentee,
  ReglementPresente,
  ReponsePdfFacture,
} from './ventes.js';

/**
 * Archive des factures — `…/archive-factures` (D21, conservation légale).
 *
 * Un routeur à part, du module du cœur `conservation-legale` : il ne sert que
 * les pièces ÉMISES, en lecture seule, que le module `ventes` soit actif ou
 * non. Aucune route d'écriture n'existe de ce côté.
 */
export interface ReponseArchiveFactures {
  readonly factures: readonly FacturePresentee[];
  readonly pagination: Pagination;
}

export interface FicheArchivee {
  readonly facture: FacturePresentee;
  readonly reglements: readonly Pick<
    ReglementPresente,
    'id' | 'montantCentimes' | 'devise' | 'mode' | 'date' | 'reference'
  >[];
  readonly avoirs: readonly AvoirRecent[];
}

/** Pièce émise avant Artosera, avec les montants et le numéro d'origine. */
export interface FactureAnterieure {
  readonly id: string;
  readonly type: 'facture' | 'avoir';
  readonly numeroOrigine: string;
  readonly emiseLe: string;
  readonly clientNom: string;
  readonly totalTtcCentimes: number;
  readonly devise: Devise;
  readonly factureOrigineNumero: string | null;
  readonly logicielOrigine: string;
}

export interface ReponseFacturesAnterieures {
  readonly factures: readonly FactureAnterieure[];
  readonly pagination: Pagination;
}

const racine = (galerie: string): string => `/g/${encodeURIComponent(galerie)}/archive-factures`;

export const archiveFacturesApi = {
  listerAnterieures: (galerie: string, page = 1, appel?: AppelApi) =>
    api.get<ReponseFacturesAnterieures>(
      `${racine(galerie)}/historiques?page=${String(page)}&parPage=50`,
      { galerie, ...appel },
    ),
  urlPdfAnterieure: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<ReponsePdfFacture>(`${racine(galerie)}/historiques/${encodeURIComponent(id)}/pdf`, {
      galerie,
      ...appel,
    }),
  lister: (galerie: string, page = 1, appel?: AppelApi) =>
    api.get<ReponseArchiveFactures>(`${racine(galerie)}?page=${String(page)}&parPage=50`, {
      galerie,
      ...appel,
    }),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<FicheArchivee>(`${racine(galerie)}/${encodeURIComponent(id)}`, {
      galerie,
      ...appel,
    }),
  urlPdf: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<ReponsePdfFacture>(`${racine(galerie)}/${encodeURIComponent(id)}/pdf`, {
      galerie,
      ...appel,
    }),
  /** Le XML Factur-X, téléchargé par le client (compte attendu, signal). */
  facturX: (galerie: string, id: string, appel?: AppelApi) =>
    requeteTelechargement(`${racine(galerie)}/${encodeURIComponent(id)}/facturx.xml`, {
      galerie,
      ...appel,
    }),
};

export function useArchiveFactures(galerie: string, page: number) {
  return useQuery({
    queryKey: cleMetier(galerie, 'archive-factures', 'liste', page),
    queryFn: (contexte) => archiveFacturesApi.lister(galerie, page, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}

export function useArchiveFacture(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleMetier(galerie, 'archive-factures', 'fiche', id ?? ''),
    queryFn: (contexte) => archiveFacturesApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

export function useFacturesAnterieures(galerie: string, page: number) {
  return useQuery({
    queryKey: cleMetier(galerie, 'archive-factures', 'anterieures', page),
    queryFn: (contexte) =>
      archiveFacturesApi.listerAnterieures(galerie, page, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}
